// FASE 05 — S4-B2/PO 2026-05-13: modal para criar nova Tabela de Precos.
//
// Relatorio do PO de 11/08/2026 (item 2) + decisao TP-1 de 19/08 (docs/DECISOES.md):
// o campo Referencia FOI REMOVIDO. A liberacao das referencias que a empresa pode
// precificar ja e feita em Gerencia > Empresa > Tabela de Preco, e o proprio universo
// de produtos da pagina de precificacao vem de TODAS as referencias liberadas — nunca
// da coluna TabelasPrecos.IdReferencia, que passa a ser gravada fixa em 36 (MERCADO).
// Sobra no modal apenas o nome da tabela; ao confirmar, abre a pagina de precificacao.

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TipoMaterialTP, TIPO_MATERIAL_TP_LABELS } from "../types/tabelaPreco.types";

interface ModalNovaTabelaProps {
  open: boolean;
  tipo: TipoMaterialTP;
  onClose: () => void;
  onConfirmar: (dados: { nome: string }) => Promise<void>;
}

export const ModalNovaTabela: React.FC<ModalNovaTabelaProps> = ({
  open,
  tipo,
  onClose,
  onConfirmar,
}) => {
  const [nome, setNome] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erroNome, setErroNome] = useState("");

  useEffect(() => {
    if (!open) {
      setNome("");
      setErroNome("");
    }
  }, [open]);

  const handleConfirmar = async () => {
    const nomeLimpo = nome.trim();
    if (!nomeLimpo) {
      setErroNome("Informe o nome da nova tabela de preços.");
      return;
    }
    setSalvando(true);
    try {
      await onConfirmar({ nome: nomeLimpo });
    } catch (erro: any) {
      // Rede de seguranca: sem este catch, qualquer rejeicao vira "o botao nao faz nada"
      // (foi o sintoma reportado pelo PO em 25/08 com o usuario ROTO FERMAX).
      setErroNome(erro?.message || "Não foi possível criar a tabela de preços.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onClose={salvando ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography component="span" sx={{ fontWeight: 600 }}>
          Nova tabela de preços — {TIPO_MATERIAL_TP_LABELS[tipo]}
        </Typography>
        <IconButton onClick={onClose} size="small" disabled={salvando}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            autoFocus
            label="Nome da tabela"
            placeholder="Informe o nome da nova tabela de preços"
            value={nome}
            onChange={(e) => {
              setNome(e.target.value);
              if (erroNome) setErroNome("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !salvando) handleConfirmar();
            }}
            error={!!erroNome}
            helperText={erroNome}
            inputProps={{ maxLength: 100 }}
            disabled={salvando}
            fullWidth
          />
          <Typography variant="body2" color="text.secondary">
            Os produtos que podem ser precificados seguem as referências liberadas para esta
            empresa em Gerência &gt; Empresas &gt; Tabela de Preços.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={salvando} sx={{ color: "#666", borderColor: "#bdbdbd" }}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmar}
          variant="contained"
          disabled={salvando}
          startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
        >
          Criar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
